import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as ExportIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  MedicalServices as MedicalIcon,
  Assessment as ChartIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart as LineChartIcon,
  Person as PersonIcon,
  LocalHospital as DoctorIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import axios from 'axios';

// Types
interface KPICard {
  title: string;
  key: string;
  value: number;
  previousValue: number;
  change: number;
  changeType: string;
  unit: string;
  color: string;
}

interface AnalyticsOverview {
  startDate: string;
  endDate: string;
  totalAppointments: number;
  completedAppointments: number;
  cancelledAppointments: number;
  pendingAppointments: number;
  completionRate: number;
  cancellationRate: number;
  totalPatients: number;
  newPatients: number;
  activeDoctors: number;
  totalDoctors: number;
  avgAppointmentsPerDoctor: number;
}

interface DoctorPerformance {
  doctorId: number;
  doctorName: string;
  specialization: string;
  totalAppointments: number;
  completedAppointments: number;
  cancelledAppointments: number;
  completionRate: number;
  totalPatients: number;
}

interface PatientStatistics {
  totalPatients: number;
  newThisMonth: number;
  activePatients: number;
  byCadre: { [key: string]: number };
}

const AnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [doctorPerformance, setDoctorPerformance] = useState<DoctorPerformance[]>([]);
  const [patientStats, setPatientStats] = useState<PatientStatistics | null>(null);
  const [kpiCards, setKpiCards] = useState<KPICard[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [dateRange, setDateRange] = useState('month');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');

      // Calculate date range
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      // Fetch all data in parallel
      const [overviewRes, doctorsRes, patientsRes, kpiRes] = await Promise.all([
        axios.get(
          `${import.meta.env.VITE_API_URL}/api/v1/admin/analytics/overview?startDate=${startDate}&endDate=${endDate}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${import.meta.env.VITE_API_URL}/api/v1/admin/analytics/doctors/performance?startDate=${startDate}&endDate=${endDate}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${import.meta.env.VITE_API_URL}/api/v1/admin/analytics/patients/statistics`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${import.meta.env.VITE_API_URL}/api/v1/admin/analytics/kpi/summary`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ]);

      if (overviewRes.data.success) {
        setOverview(overviewRes.data.data);
      }
      if (doctorsRes.data.success) {
        setDoctorPerformance(doctorsRes.data.data);
      }
      if (patientsRes.data.success) {
        setPatientStats(patientsRes.data.data);
      }
      if (kpiRes.data.success) {
        setKpiCards(kpiRes.data.data.cards || []);
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatPercent = (num: number) => {
    return num.toFixed(1) + '%';
  };

  // KPI Cards from API
  const kpiCardsDisplay = kpiCards.length > 0 ? kpiCards.map(card => ({
    title: card.title,
    value: card.value,
    change: card.change,
    changeType: card.changeType,
    icon: card.key === 'total_appointments' ? <CalendarIcon /> :
         card.key === 'completed_appointments' ? <ChartIcon /> :
         card.key === 'total_patients' ? <PeopleIcon /> : <MedicalIcon />,
    color: card.color,
  })) : [
    { title: 'Total Appointments', value: overview?.totalAppointments || 0, change: 12.5, changeType: 'increase', icon: <CalendarIcon />, color: '#2563eb' },
    { title: 'Completed', value: overview?.completedAppointments || 0, change: 8.3, changeType: 'increase', icon: <ChartIcon />, color: '#10b981' },
    { title: 'Total Patients', value: overview?.totalPatients || 0, change: 5.2, changeType: 'increase', icon: <PeopleIcon />, color: '#8b5cf6' },
    { title: 'Active Doctors', value: overview?.activeDoctors || 0, change: 0, changeType: 'neutral', icon: <MedicalIcon />, color: '#06b6d4' },
    { title: 'Completion Rate', value: overview?.completionRate || 0, change: 2.1, changeType: 'increase', icon: <TrendingUpIcon />, color: '#f59e0b', isPercent: true },
    { title: 'Avg per Doctor', value: overview?.avgAppointmentsPerDoctor || 0, change: -3.2, changeType: 'decrease', icon: <LineChartIcon />, color: '#ef4444' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Analytics & Reporting Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Comprehensive insights and performance metrics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="week">Last 7 Days</MenuItem>
              <MenuItem value="month">Last 30 Days</MenuItem>
              <MenuItem value="quarter">Last 90 Days</MenuItem>
              <MenuItem value="year">Last Year</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchData()}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCardsDisplay.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', height: '100%' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                    {card.title}
                  </Typography>
                  <Box sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: `${card.color}15`,
                    color: card.color,
                  }}>
                    {card.icon}
                  </Box>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: card.color }}>
                  {card.isPercent ? formatPercent(card.value) : formatNumber(card.value)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  {card.changeType === 'increase' && <TrendingUpIcon sx={{ fontSize: 16, color: '#10b981' }} />}
                  {card.changeType === 'decrease' && <TrendingDownIcon sx={{ fontSize: 16, color: '#ef4444' }} />}
                  <Typography
                    variant="caption"
                    sx={{
                      color: card.changeType === 'increase' ? '#10b981' :
                             card.changeType === 'decrease' ? '#ef4444' : '#64748b',
                      ml: 0.5
                    }}
                  >
                    {card.changeType !== 'neutral' && `${card.change > 0 ? '+' : ''}${card.change.toFixed(1)}%`}
                    {card.changeType === 'neutral' && 'No change'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', ml: 0.5 }}>
                    vs last period
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: '1px solid #e2e8f0' }}>
          <Tab label="Overview" icon={<ChartIcon />} sx={{ textTransform: 'none' }} />
          <Tab label="Doctors" icon={<DoctorIcon />} sx={{ textTransform: 'none' }} />
          <Tab label="Patients" icon={<PeopleIcon />} sx={{ textTransform: 'none' }} />
          <Tab label="Trends" icon={<LineChartIcon />} sx={{ textTransform: 'none' }} />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ p: 3 }}>
          {loading ? (
            <LinearProgress />
          ) : activeTab === 0 ? (
            /* Overview Tab */
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Appointment Overview
                </Typography>
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'transparent' , borderRadius: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <BarChartIcon sx={{ fontSize: 60, color: '#cbd5e1' }} />
                    <Typography sx={{ color: '#94a3b8', mt: 1 }}>
                      Chart visualization coming soon
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Status Distribution
                </Typography>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Completed</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatPercent(overview?.completionRate || 0)}</Typography>
                    </Box>
                    <Box sx={{ height: 8, bgcolor: 'transparent' , borderRadius: 1 }}>
                      <Box sx={{ height: '100%', width: `${overview?.completionRate || 0}%`, bgcolor: 'transparent' , borderRadius: 1 }} />
                    </Box>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Cancelled</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatPercent(overview?.cancellationRate || 0)}</Typography>
                    </Box>
                    <Box sx={{ height: 8, bgcolor: 'transparent' , borderRadius: 1 }}>
                      <Box sx={{ height: '100%', width: `${overview?.cancellationRate || 0}%`, bgcolor: 'transparent' , borderRadius: 1 }} />
                    </Box>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Pending</Typography>
                      <Typography variant="body2" fontWeight={600}>{overview?.pendingAppointments || 0}</Typography>
                    </Box>
                    <Box sx={{ height: 8, bgcolor: 'transparent' , borderRadius: 1 }}>
                      <Box sx={{ height: '100%', width: '30%', bgcolor: 'transparent' , borderRadius: 1 }} />
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          ) : activeTab === 1 ? (
            /* Doctors Tab */
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Doctor Performance
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'transparent' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Doctor</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Specialization</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Appointments</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Completed</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Cancelled</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Completion Rate</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Patients</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {doctorPerformance.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography sx={{ color: '#64748b' }}>No doctor performance data available</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      doctorPerformance.map((doctor) => (
                        <TableRow key={doctor.doctorId} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 36, height: 36, bgcolor: 'transparent' }}>
                                {doctor.doctorName?.charAt(0) || 'D'}
                              </Avatar>
                              <Typography fontWeight={500}>{doctor.doctorName}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={doctor.specialization} size="small" sx={{ borderRadius: 1 }} />
                          </TableCell>
                          <TableCell align="center">{doctor.totalAppointments}</TableCell>
                          <TableCell align="center">
                            <Typography sx={{ color: '#10b981', fontWeight: 600 }}>
                              {doctor.completedAppointments}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography sx={{ color: '#ef4444' }}>
                              {doctor.cancelledAppointments}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <Box sx={{ width: 60, height: 6, bgcolor: 'transparent' , borderRadius: 1 }}>
                                <Box sx={{ height: '100%', width: `${doctor.completionRate}%`, bgcolor: 'transparent' , borderRadius: 1 }} />
                              </Box>
                              <Typography variant="body2">{formatPercent(doctor.completionRate)}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">{doctor.totalPatients}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : activeTab === 2 ? (
            /* Patients Tab */
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Patient Statistics
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>Total Patients</Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: '#2563eb' }}>
                        {patientStats?.totalPatients || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>New This Month</Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: '#10b981' }}>
                        {patientStats?.newThisMonth || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>Active Patients</Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
                        {patientStats?.activePatients || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Patients by Cadre
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {patientStats?.byCadre && Object.entries(patientStats.byCadre).map(([cadre, count]) => (
                      <Chip
                        key={cadre}
                        label={`${cadre}: ${count}`}
                        sx={{ borderRadius: 1, fontWeight: 500 }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ) : (
            /* Trends Tab */
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Appointment Trends
              </Typography>
              <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'transparent' , borderRadius: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <LineChartIcon sx={{ fontSize: 80, color: '#cbd5e1' }} />
                  <Typography sx={{ color: '#94a3b8', mt: 2 }}>
                    Trend charts coming soon
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    Daily, weekly, and monthly trends will be displayed here
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AnalyticsPage;